import { api, API_BASE_URL } from "./api";

import type {
  LoginResult,
  AuthResponse,
  UserProfile,
  DeviceInfo,
} from "@/types/auth";

export const isElectronGuest = (): boolean =>
  typeof window !== "undefined" && Boolean(window.electronAPI);

class AuthService {
  async login(
    email: string,

    password: string,

    totpCode?: string,
  ): Promise<LoginResult> {
    return api.post<LoginResult>("/auth/login", {
      email,

      password,

      ...(totpCode ? { totpCode } : {}),
    });
  }

  async register(
    name: string,

    lastName: string,

    email: string,

    password: string,

    language: string,

    captchaToken?: string,
    // El backend exige `termsAccepted === true` y sin eso no crea la cuenta.
    // Default `false` (no `true`): si una pantalla se lo olvida, el alta falla
    // con un 400 explicito en vez de colarse como si se hubiera aceptado.
    termsAccepted = false,
  ): Promise<AuthResponse> {
    return api.post<AuthResponse>("/auth/register", {
      termsAccepted,

      name,

      lastName,

      email,

      password,

      language,

      ...(captchaToken ? { captchaToken } : {}),
    });
  }

  async forgotPassword(
    email: string,
  ): Promise<{ success: boolean; message: string }> {
    return api.post("/auth/forgot-password", { email });
  }

  async logout(): Promise<void> {
    await api.authPost("/auth/logout", {});
  }

  /**
   * Trades the Electron-local accessKey for HttpOnly session cookies in
   * the WebView. Called after a deep-link auth lands so subsequent calls
   * can ride on the cookie path (closer to the browser PWA model).
   *
   * No-op-ish failure mode: if the upgrade fails we keep the existing
   * Authorization header path and the user remains logged in.
   */
  async upgradeSession(): Promise<void> {
    await api.authPost("/auth/session/upgrade", undefined);
  }

  /**
   * La URL con la que arranca el OAuth de Google.
   *
   * NO lleva la constancia de términos, y no es un olvido: viajaba acá como
   * `?termsAccepted=true`, o sea que la declaraba quien armaba la URL y no la
   * persona. Si la cuenta no existe, el backend ya no crea nada: manda a
   * completar el alta en una pantalla de eriol.io, donde se acepta de verdad.
   */
  getGoogleAuthUrl(): string {
    // Electron registra el protocolo eriol-guest://; la PWA no puede, así que
    // le decimos al backend que vuelva por HTTPS a su /auth-callback.
    const params = new URLSearchParams({
      from: isElectronGuest() ? "guest" : "guest-pwa",
    });

    return `${API_BASE_URL}/auth/google?${params.toString()}`;
  }

  isElectron(): boolean {
    return isElectronGuest();
  }

  async verifyGoogleTwoFactor(
    pendingToken: string,

    totpCode: string,
  ): Promise<AuthResponse> {
    return api.post<AuthResponse>("/auth/2fa/verify-google-login", {
      pendingToken,

      totpCode,
    });
  }

  async getMe(): Promise<UserProfile> {
    const result = await api.authGet<{ data: UserProfile }>("/auth/me");

    return result.data;
  }

  async completeOnboarding(accountType: string): Promise<UserProfile> {
    const result = await api.authPost<{ data: UserProfile }>(
      "/auth/onboarding",

      { accountType },
    );

    return result.data;
  }

  async getDevices(): Promise<DeviceInfo[]> {
    const result = await api.authGet<{ data: DeviceInfo[] }>("/auth/devices");

    return result.data;
  }

  async revokeDevice(tokenId: string): Promise<void> {
    await api.authPost(`/auth/devices/${tokenId}/revoke`, {});
  }

  async validateToken(): Promise<boolean> {
    try {
      await this.getMe();

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "";

      if (message.includes("401") || message.includes("Unauthorized")) {
        return false;
      }

      // Network error / backend down — assume session is still valid

      return true;
    }
  }
}

export const authService = new AuthService();

export default authService;
